import { Project } from './project';
import { FilterPlan } from './common';
import { Customer } from './customer';
import { httpClient, QueryParams } from './httpClient';
import { Objective, SearchObjective, SearchPlanAccumulation } from '../types/objective';

export interface ActionPlanStatistic {
  total: number;
  notStarted: number;
  completed: number;
  completionRate: number;
}

export interface PlanStatistic {
  actionPlanStatistic?: ActionPlanStatistic;
  // 可扩展其他统计字段
}

export interface Plan extends Objective {
  challengeTarget?: number;
  previousPlan?: Plan;
  projects?: Project[];
  objectiveType: string;
  statistic?: PlanStatistic;
  customer?: Customer;
  varianceAnalysisId?: string;
  startDate?: string;
  endDate?: string;
}

export interface PlanList {
  total: number;
  page: number;
  size: number;
  records: Plan[];
  statistic: {
    total: number;
    notStarted: number;
    inProgress: number;
    completed: number;
    canceled: number;
    completionRate: number;
  };
}

export interface PlanAccumulation {
  target: number | null;
  operationalStatementCurrent: number | null;
  departmentId: string;
  subjectId: string;
  customerId: string | null;
  frequencyType: string;
}

export interface GetAllPlansParams {
  page?: number;
  size?: number;
}

export async function getPlan(id: string): Promise<Plan> {
  const response = await httpClient.sendRequest({
    url: `plans/${id}`,
    method: 'GET',
  });
  return response.data;
}

export async function createPlan(payload: Partial<Omit<Plan, 'id'>>): Promise<Plan> {
  const response = await httpClient.sendRequest({
    url: `plans`,
    method: 'POST',
    data: payload,
  });
  return response.data;
}

export async function updatePlan(payload: Partial<Plan>): Promise<Plan> {
  const response = await httpClient.sendRequest({
    url: `plans`,
    method: 'PUT',
    data: payload,
  });
  return response.data;
}

export async function deletePlan(payload: Partial<Plan>): Promise<Plan> {
  const response = await httpClient.sendRequest({
    url: `plans`,
    method: 'DELETE',
    data: payload,
  });
  return response.data;
}

export async function searchPlan(payload: SearchObjective): Promise<Plan[]> {
  const params = new URLSearchParams();
  Object.entries(payload).forEach(([key, value]) => {
    if (key !== 'sequences') {
      if (Array.isArray(value)) {
        value.forEach((item) => {
          params.append(key, String(item));
        });
      } else if (value !== undefined) {
        params.append(key, String(value));
      }
    } else {
      params.set('sequences', value.join(','));
    }
  });
  const requestUrl = `plans/search?${params.toString()}`;

  const response = await httpClient.sendRequest({
    url: requestUrl,
    method: 'GET',
  });
  return response.data;
}

export async function getPlanAccumulation(
  payload: SearchPlanAccumulation
): Promise<PlanAccumulation[]> {
  const params = new URLSearchParams();

  Object.entries(payload).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item) => {
        params.append(key, String(item));
      });
    } else if (value !== undefined) {
      params.append(key, String(value));
    }
  });

  const requestUrl = `plans/accumulation?${params.toString()}`;

  const response = await httpClient.sendRequest({
    url: requestUrl,
    method: 'GET',
  });
  return response.data;
}

export async function createBulkPlan(payload: Partial<Omit<Plan, 'id'>>[]): Promise<Plan> {
  const response = await httpClient.sendRequest({
    url: `plans/bulk`,
    method: 'POST',
    data: payload,
  });
  return response.data;
}

export async function createBulkUpsertPlan(payload: Partial<Omit<Plan, 'id'>>[]): Promise<Plan> {
  const response = await httpClient.sendRequest({
    url: `plans/bulk_upsert`,
    method: 'POST',
    data: payload,
  });
  return response.data;
}

export async function getAllPlans(params?: GetAllPlansParams): Promise<PlanList> {
  const queryParams: QueryParams = {};

  if (params?.page !== undefined) {
    queryParams.page = params.page.toString();
  }

  if (params?.size !== undefined) {
    queryParams.size = params.size.toString();
  }

  const response = await httpClient.sendRequest({
    url: `plans`,
    method: 'GET',
    queryParams: Object.keys(queryParams).length > 0 ? queryParams : undefined,
  });

  return response.data;
}

export async function filterPlans(
  payload: FilterPlan,
  params?: GetAllPlansParams
): Promise<PlanList> {
  const queryParams: Record<string, string> = {};

  if (params?.page !== undefined) {
    queryParams.page = params.page.toString();
  }

  if (params?.size !== undefined) {
    queryParams.size = params.size.toString();
  }

  // 将targetType从payload中提取出来，放到URL参数中
  if (payload.targetType) {
    queryParams.targetType = payload.targetType;
  }

  // 从payload中移除targetType，避免重复发送
  const { targetType, ...payloadWithoutTargetType } = payload;

  const response = await httpClient.sendRequest({
    url: 'plans/search',
    method: 'POST',
    data: payloadWithoutTargetType,
    queryParams: Object.keys(queryParams).length > 0 ? queryParams : undefined,
  });
  return response.data;
}
